import React, { useId, useMemo, useRef, useState } from "react"
import { Box, Icon, Popover } from "@mui/material"
import { DateRangePicker } from "mui-daterange-picker"
import format from "date-fns/format"
import { enAU, id } from "date-fns/locale"
import { useTranslation } from "react-i18next"
import useLng from "../zustand/languageZustand"
import { CButton } from "./StyledComponent"
import { FIRSTDAY_OF_MONTH } from "../utils/Constant"
import { isEmpty } from "../utils/Helper"

const btnStyle = {
	display: "flex",
	position: "relative",
	alignItems: "center",
	border: "1px solid lightgrey",
	borderRadius: "0.2rem",
	paddingBlock: "2px",
	margin: "0.2rem 0",
	"&:hover": { borderColor: "var(--primary_color)" },
	legend: {
		position: "absolute",
		top: "-0.63rem",
		left: "0.2rem",
		fontSize: "0.8rem",
		backgroundColor: "white",
		fontWeight: "bold",
		padding: "0 0.5rem",
		color: "var(--primary_color)",
	},
	button: {
		display: "flex",
		whiteSpace: "nowrap",
	},
}

function CDateRangePicker({
	label = "common.date_range", startDate = undefined, endDate = undefined, defNull = false,
	onChange, ...props
}) {
	const key = useId()
	const ref = useRef(null)
	const { t } = useTranslation()
	const { lng } = useLng()
	const locale = useMemo(() => (lng === "id" ? id : enAU), [lng])

	const _startDate = startDate ?? (defNull ? null : FIRSTDAY_OF_MONTH)
	const _endDate = endDate ?? (defNull ? null : new Date())
	const def_value = { startDate: _startDate, endDate: _endDate }
	const input_label = isEmpty(def_value?.startDate) ? "DD/MM/YYYY - DD/MM/YYYY" : `${format(def_value.startDate, "dd/MM/yyyy")} - ${format(def_value.endDate, "dd/MM/yyyy")}`

	const [toggle, setToggle] = useState(false)
	const handleToggle = (_toggle = !toggle) => setToggle(_toggle)
	const anchorEl = useMemo(() => (toggle ? ref.current : null), [toggle, ref])

	const handleCLear = () => {
		if (onChange) onChange({ startDate: null, endDate: null })
	}

	const _onChange = (range) => {
		if (onChange) onChange(range)
		handleToggle(false)
	}

	const open = Boolean(anchorEl)
	const idx = open ? "simple-popover" : undefined
	const disabledFlag = props?.disabled ?? false
	const cursor = disabledFlag ? "not-allowed" : "pointer"

	return (
		<Box key={key} sx={{ flex: "1 1 50%", ...props.warpperStyle, cursor }}>
			<Box sx={{ ...btnStyle, ...props.btnStyle, justifyContent: "space-between", alignItems: "center" }}>
				<legend style={{ textTransform: "capitalize" }}>{t(label)}</legend>
				<CButton
					ref={ref}
					txt_color="black"
					variant="text"
					sx={{ width: "100%", minWidth: 'fit-content', justifyContent: "space-between" }}
					onClick={() => handleToggle()}
					disabled={disabledFlag}
					disabled_style={{ backgroundColor: 'transparent', color: 'black' }}
				>
					{input_label}
				</CButton>
				<Box sx={{ display: 'flex', gap: '0.5rem', paddingInline: '0.5rem' }}>
					{defNull && <Icon onClick={handleCLear}>close</Icon>}
					<Icon onClick={() => handleToggle()}>calendar_month_icon</Icon>
				</Box>
			</Box>

			<Popover
				id={idx}
				anchorEl={anchorEl}
				open={toggle}
				onClose={() => handleToggle()}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				sx={{ "& .MuiBox-root:nth-of-type(2)": { width: "fit-content" } }}
			>
				<DateRangePicker
					open={toggle}
					initialDateRange={def_value}
					toggle={() => handleToggle()}
					closeOnClickOutside
					definedRanges={[]}
					onChange={_onChange}
					{...props}
					locale={locale}
				/>
			</Popover>
		</Box>
	)
}

export default CDateRangePicker